// A categoria AGÊNCIA e o serviço FEE AGÊNCIA não são identificados pelo código
// 130 isolado: o catálogo também contém, por exemplo, "limpeza" com esse código.
const ID_GRUPO_AGENCIA = '939529851763065082';
const nomeCatalogo = (produto = {}) => String(produto?.nome ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim().toUpperCase().replace(/^\d+\s*-\s*/, '').trim().replace(/\s+/g, ' ');
const codigo = (produto = {}) => String(produto?.codigo ?? '').trim();
const id = (valor) => String(valor && typeof valor === 'object' ? (valor.id ?? '') : (valor ?? ''));

const isGrupoAgencia = (grupo = {}) => {
    const produto = grupo?.produto ?? {};
    if (codigo(produto) === '3027' || nomeCatalogo(produto) === 'SERVICOS AGENCIA') return false;
    return id(produto) === ID_GRUPO_AGENCIA
        || (nomeCatalogo(produto) === 'AGENCIA' && (!codigo(produto) || codigo(produto) === '130'));
};

const isGrupoServicosAgencia = (grupo = {}) => codigo(grupo?.produto) === '3027'
    || nomeCatalogo(grupo?.produto) === 'SERVICOS AGENCIA';

const isFeeAgenciaGlobal = (produto = {}) => codigo(produto) === '130'
    && nomeCatalogo(produto) === 'FEE AGENCIA';

// Fees de catálogo do cliente: Sanofi (2043, contratação/gerência) e Abbvie
// (2130-2136, "Fee da Agência - <categoria>" com faixas por pax). O 143
// (FEE DA AGÊNCIA NA CONTRATAÇÃO, retirado dos orçamentos) nunca é opção.
const isFeeDeCatalogo = (produto = {}) => codigo(produto) !== '143'
    && (codigo(produto) === '2043' || nomeCatalogo(produto).indexOf('FEE DA AGENCIA') === 0);

// Apresentação apenas: não renomeia o catálogo nem os descritores salvos.
const nomeGrupoExibicao = (produto = {}, incluirCodigo = false) => {
    const raiz = !id(produto?.superior);
    if (raiz && isGrupoAgencia({ produto })) return '130 - FEE';
    if (!incluirCodigo || /^\d+\s*-\s*/.test(String(produto?.nome ?? ''))) return produto?.nome ?? '';
    return (codigo(produto) ? `${codigo(produto)} - ` : '') + (produto?.nome ?? '');
};

// FEE continua procurando os grupos FEE reais; a segunda consulta só encontra
// o grupo cujo nome canônico continua sendo AGÊNCIA.
const termosBuscaGrupos = (input = '') => String(input ?? '').trim().toUpperCase() === 'FEE'
    ? [input, 'AGÊNCIA'] : [input];

const mesclarResultadosGrupos = (resultados = []) => {
    const ids = new Set();
    return resultados.reduce((grupos, registros, index) => {
        (registros || []).forEach((registro) => {
            // A consulta complementar AGÊNCIA serve apenas ao alias 130;
            // SERVIÇOS AGÊNCIA e demais homônimos não são resultados de FEE.
            if (index > 0 && !isGrupoAgencia({ produto: registro })) return;
            const registroId = id(registro);
            if (!registroId || ids.has(registroId)) return;
            ids.add(registroId);
            grupos.push(registro);
        });
        return grupos;
    }, []);
};

// Recebe somente o catálogo já autorizado para o cliente pela API. Não deve
// ser usado nas linhas históricas, na sincronização por IDs ou nos cálculos.
const filtrarOpcoesCatalogo = (grupo = {}, produtos = [], clienteEscolheFee = false) => {
    if (!isGrupoAgencia(grupo)) return produtos;
    // Cliente com fee de catálogo (2043) nunca recebe o 130 global na lista nova:
    // além de duplicar o nome "Fee Agência", selecioná-lo faz a linha sumir no
    // recálculo (a política remove o 130 para Sanofi/Abbvie). A checagem por
    // presença do 2043 na resposta não basta — uma busca por texto pode devolver
    // só o 130 — por isso o corte também vale pelo perfil do cliente.
    // Regra da taxonomia: o picker do 130 - FEE oferece SOMENTE "Fee Agência" e
    // "Imposto", para todos os clientes. Fees por categoria (Abbvie 2130-2136)
    // são tabela de cálculo do motor, nunca opções de linha.
    const temFeeCatalogo = produtos.some((produto) => codigo(produto) === '2043');
    return produtos.filter((produto) => {
        const nome = nomeCatalogo(produto);
        if (isFeeAgenciaGlobal(produto)) return !clienteEscolheFee && !temFeeCatalogo;
        return codigo(produto) === '2043'
            || ['IMPOSTO', 'IMPOSTOS'].includes(nome);
    });
};

// Serviços da agência cobrados por cliente (Abbvie 2137-2143/2162 com preço por
// faixa, Sanofi ACCESS10xx) vivem no catálogo sob o grupo AGÊNCIA, cujo picker
// só oferece Fee e Imposto. Por isso o picker de SERVIÇOS AGÊNCIA (3027) também
// consulta o AGÊNCIA e aproveita apenas os itens exclusivos do cliente com preço
// fixo: fees (2043, 2130-2136), Imposto e os globais do 130 continuam fora.
const temClienteExclusivo = (produto = {}) => {
    const cliente = produto?.cliente;
    if (Array.isArray(cliente)) return cliente.length > 0;
    return String(cliente ?? '').trim() !== '';
};

// Recebe a resposta da consulta com superior = AGÊNCIA (o filtro de grupo e de
// cliente já foi feito pela API).
const isServicoAgenciaDoCliente = (produto = {}) => temClienteExclusivo(produto)
    && String(produto?.tipoPreco ?? 'fixo') !== 'percentual'
    && !isFeeDeCatalogo(produto)
    && !isFeeAgenciaGlobal(produto)
    && !['IMPOSTO', 'IMPOSTOS'].includes(nomeCatalogo(produto));

// Superior da consulta extra do picker, ou null quando o grupo não a usa.
const superiorServicosDoCliente = (grupo = {}) => (isGrupoServicosAgencia(grupo) ? ID_GRUPO_AGENCIA : null);

const filtrarServicosDoCliente = (produtos = []) => (produtos || []).filter(isServicoAgenciaDoCliente);

// O 2043 chama-se "Fee da agência na contratação..." no catálogo, mas é exibido
// como "Fee Agência": quem digita esse rótulo não o encontraria pelo LIKE.
// No grupo AGÊNCIA, um input com FEE ganha a consulta ampla "fee" (o filtro
// de opções corta os estranhos depois).
const termosBuscaSubitens = (grupo = {}, input = '') => {
    const texto = String(input ?? '').trim();
    if (!isGrupoAgencia(grupo)) return [texto];
    const normalizado = texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
    if (normalizado.includes('FEE') && normalizado !== 'FEE') return [texto, 'fee'];
    return [texto];
};

const nomeSubitemExibicao = (grupo = {}, produto = {}, incluirCodigo = false) => {
    if (isGrupoAgencia(grupo)) {
        if (isFeeAgenciaGlobal(produto)) return 'Fee Agência';
        if (codigo(produto) === '2043') return 'Fee Agência';
    }
    if (!incluirCodigo) return produto?.nome ?? '';
    return (codigo(produto) ? `${codigo(produto)} - ` : '')
        + (produto?.codigoFornecedor ? `${produto.codigoFornecedor} - ` : '') + (produto?.nome ?? '');
};

const mesmaLinha = (a, b) => {
    const idA = id(a);
    const idB = id(b);
    if (idA && idB) return idA === idB;
    if (idA || idB) return false;
    return Boolean(a?.chaveunica) && String(a.chaveunica) === String(b?.chaveunica);
};

// Muta a árvore como o fluxo existente. Factories ficam na tela para preservar
// os padrões de produto, chave e valores; não cria nem salva registros.
const garantirEstrutura = (grupos = [], { incluirFeePadrao = true, criarGrupo, criarFee } = {}) => {
    if (!Array.isArray(grupos)) return;
    let agencia = grupos.find(isGrupoAgencia);
    if (!agencia) {
        // Primeiro move eventual FEE legado; a factory não deve injetar outro.
        agencia = criarGrupo(false);
        grupos.unshift(agencia);
    }
    agencia.subitem = Array.isArray(agencia.subitem) ? agencia.subitem : [];
    if (!incluirFeePadrao) {
        agencia.subitem = agencia.subitem.filter((linha) => !isFeeAgenciaGlobal(linha?.produto));
        return;
    }
    grupos.filter(isGrupoServicosAgencia).forEach((grupo) => {
        const permanecer = [];
        (grupo.subitem || []).forEach((linha) => {
            if (!isFeeAgenciaGlobal(linha?.produto)) {
                permanecer.push(linha);
                return;
            }
            if (!agencia.subitem.some((existente) => mesmaLinha(existente, linha))) {
                // Se a tela criou um fallback sem ID, preferir a linha já salva.
                if (id(linha)) {
                    agencia.subitem = agencia.subitem.filter((existente) => id(existente) || !isFeeAgenciaGlobal(existente?.produto));
                } else if (agencia.subitem.some((existente) => isFeeAgenciaGlobal(existente?.produto))) {
                    return;
                }
                agencia.subitem.push(linha);
            }
        });
        grupo.subitem = permanecer;
    });
    if (!agencia.subitem.some((linha) => isFeeAgenciaGlobal(linha?.produto))) {
        agencia.subitem.unshift(criarFee());
    }
};

export const AgenciaOrcamentoPolicy = { isGrupoAgencia, isGrupoServicosAgencia, isFeeAgenciaGlobal, isFeeDeCatalogo, isServicoAgenciaDoCliente, superiorServicosDoCliente, filtrarServicosDoCliente, garantirEstrutura, nomeGrupoExibicao, nomeSubitemExibicao, filtrarOpcoesCatalogo, termosBuscaSubitens, termosBuscaGrupos, mesclarResultadosGrupos };
